/** @flow */
import React, { Component } from 'react';
import styled from 'styled-components';
import { BarChart, Bar, XAxis, YAxis, Legend, ResponsiveContainer, Brush, Tooltip, CartesianGrid } from 'recharts';
import { translate } from 'react-i18next';
import _ from 'lodash';

import { roundNumber } from 'common/helpers/math';
import MetricRechartsTooltip from 'common/components/charts/tooltip/MetricRechartsTooltip';
import { Colors } from '../../../themes';

const GraphWrapper = styled.div`
  ${props => props.style}
  width: 100%;
  height: 100%;
  max-width: 100%;
  display: flex;
`;

type Props = {
  reach: Object;
  listeningTime: Object;
  type: 'reach' | 'listeningTime';
  t: any;
  style?: Object;
  hideInteraction?: boolean;
};

type State = {
  reachChartData: Array<Object>;
  listeningTimeChartData: Array<Object>;
};

class HourlyMetricsGraph extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      reachChartData: this.convertReachForChart(props.reach),
      listeningTimeChartData: this.convertListeningTimeForChart(props.listeningTime),
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    if (!_.isEqual(this.props.reach, nextProps.reach)) {
      this.setState({ reachChartData: this.convertReachForChart(nextProps.reach) });
    }

    if (!_.isEqual(this.props.listeningTime, nextProps.listeningTime)) {
      this.setState({ listeningTimeChartData: this.convertListeningTimeForChart(nextProps.listeningTime) });
    }
  }

  convertReachForChart(reachData: Object) {
    const { t } = this.props;
    const data = [];
    Object.keys(reachData)
      .sort()
      .forEach((key) => {
        const current = reachData[key].current;
        const compared = reachData[key].compared;
        const difference = current - compared;
        const value = {};
        if (difference >= 0) {
          value.name = key;
          value[t('reach')] = current - difference;
          value[t('increase')] = difference;
        } else {
          value.name = key;
          value[t('reach')] = current;
          value[t('decrease')] = Math.abs(difference);
        }
        data.push(value);
      });
    return data;
  }

  convertListeningTimeForChart(listeningTimeData: Object) {
    const { t } = this.props;
    const data = [];
    Object.keys(listeningTimeData)
      .sort()
      .forEach((key) => {
        const current = roundNumber(listeningTimeData[key].current, 2);
        const compared = roundNumber(listeningTimeData[key].compared, 2);
        const difference = current - compared;
        const value = {};
        if (difference >= 0) {
          value.name = key;
          value[t('listeningInHours')] = current - difference;
          value[t('increase')] = difference;
        } else {
          value.name = key;
          value[t('listeningInHours')] = current;
          value[t('decrease')] = Math.abs(difference);
        }
        data.push(value);
      });
    return data;
  }

  getData = () => {
    if (this.props.type === 'listeningTime') {
      return this.props.listeningTime;
    }

    return this.props.reach;
  };

  render() {
    const { t, hideInteraction, style } = this.props;
    const data = this.getData();

    const keyForDecrease = t('decrease');
    const keyForIncrease = t('increase');
    const mainBarKey = this.props.type === 'listeningTime' ? t('listeningInHours') : t('reach');
    const chartData = this.props.type === 'listeningTime' ? this.state.listeningTimeChartData : this.state.reachChartData;

    return (
      <GraphWrapper style={style}>
        <ResponsiveContainer>
          <BarChart data={chartData} barSize={80} width={100}>
            <CartesianGrid stroke={Colors.graph.lines} strokeDasharray="5 5" vertical={false} />
            <XAxis dataKey="name" height={30} />
            <YAxis />
            {!hideInteraction &&
            <Tooltip content={<MetricRechartsTooltip data={data} dataKey={mainBarKey} type={this.props.type} />} />
            }
            <Legend
              wrapperStyle={{ top: -15 }}
              verticalAlign="top"
              iconSize={30}
              height={20}
            />
            <Brush
              dataKey="name"
              height={hideInteraction ? 0 : 40}
              stroke={Colors.primary}
              startIndex={5}
              endIndex={22}
            />
            <Bar
              dataKey={mainBarKey}
              stackId="a"
              fill={Colors.primary}
              legendType="none"
              animationEasing="ease-in-out"
              animationDuration={1000}
              isAnimationActive={!hideInteraction}
            />
            <Bar
              dataKey={keyForDecrease}
              stackId="a"
              fill={Colors.decreaseStats}
              legendType="circle"
              animationEasing="ease-in-out"
              animationDuration={1000}
              isAnimationActive={!hideInteraction}
            />
            <Bar
              dataKey={keyForIncrease}
              stackId="a"
              fill={Colors.increaseStats}
              legendType="circle"
              animationEasing="ease-in-out"
              animationDuration={1000}
              isAnimationActive={!hideInteraction}
            />
          </BarChart>
        </ResponsiveContainer>
      </GraphWrapper>
    );
  }
}

export default translate(['dashboard', 'general'])(HourlyMetricsGraph);



// WEBPACK FOOTER //
// ./src/statsradio/modules/dashboard/components/HourlyMetricsGraph.js